import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { queryClient } from "App";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import { getNextQueryParams } from "components/helpers";
import useMetaSyncInProgress, {
  META_SYNC_POLL_INTERVAL_MS,
} from "hooks/useMetaSyncInProgress";
import MetaDatasetService from "services/MetaDataset";

const datasetsQueryKey = accountId => ["meta-datasets", accountId];

const DATASET_COLUMNS = [
  {
    header: "ID",
    id: "ID",
    accessorKey: "id",
    size: 50,
    cell: ({ row }) => (
      <p style={{ fontWeight: "bold" }}>{row.getValue("ID")}</p>
    ),
  },
  {
    id: "Dataset ID",
    accessorKey: "dataset_id",
    header: "Dataset ID",
    size: 200,
  },
  { id: "Name", accessorKey: "name", header: "Name", size: 220 },
  {
    id: "CRM",
    accessorKey: "is_crm",
    header: "CRM",
    size: 100,
    cell: ({ row }) =>
      row.getValue("CRM") ? <Badge variant="success">CRM</Badge> : null,
  },
  {
    id: "Owner Business",
    accessorKey: "owner_business",
    header: "Owner Business",
    size: 220,
  },
];

const MetaDatasetsTab = () => {
  const { accountId } = useParams();

  const columns = useMemo(() => DATASET_COLUMNS, []);
  const isSyncing = useMetaSyncInProgress(accountId, "live");

  const invalidateDatasets = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: datasetsQueryKey(accountId),
      }),
    [accountId]
  );

  const fetchDatasets = useCallback(
    async queryParams => {
      const next = getNextQueryParams(queryParams, columns);
      return MetaDatasetService.list(accountId, next);
    },
    [accountId, columns]
  );

  return (
    <div className="tw:flex tw:min-h-0 tw:min-w-0 tw:flex-1 tw:flex-col">
      <BaseTableV3
        columns={columns}
        header="Datasets"
        tableName="meta_datasets"
        queryKey={datasetsQueryKey(accountId)}
        fetchData={fetchDatasets}
        invalidateData={invalidateDatasets}
        refetchInterval={isSyncing ? META_SYNC_POLL_INTERVAL_MS : undefined}
        enableSearch
        enableSettings
      />
    </div>
  );
};

export default MetaDatasetsTab;
